import { View } from "react-native";
import { List, useTheme, Text, Divider, IconButton } from "react-native-paper";
import { useTailwind } from "tailwind-rn";

import InfoText from "../../components/InfoText";
import useDeliveryFulfillments from "../../contexts/DeliveryFulfillmentsContext";
import { FulfillmentDispatchType } from "../../types";

type Props = {
  navigateToAutomaticFulfillment: (type: FulfillmentDispatchType) => void;
};

export default function FulfillmentOptions({
  navigateToAutomaticFulfillment,
}: Props) {
  const tailwind = useTailwind();
  const { colors } = useTheme();

  const flags = useFulfillmentVisibility();

  const detailsText = flags.hasOptions
    ? "Al activar un método de envío, tus clientes en las áreas de cobertura podrán elegirlo al comprar en tu tienda."
    : "La dirección de recogida esta fuera del área de cobertura para la recolección de paquetes.";

  return (
    <View style={tailwind("gap-4")}>
      <InfoText
        title="Métodos de envío"
        details={detailsText}
        style={tailwind("gap-2")}
        detailsLeft={
          !flags.hasOptions && (
            <IconButton
              icon="information-outline"
              iconColor={colors.onSurfaceVariant}
              containerColor={colors.errorContainer}
            />
          )
        }
      />

      <View>
        {flags.on_demand.visible && (
          <Item
            title="Envío directo"
            description="⚡️ Retiro inmediato. Entrega hoy. Ciudad de Panama"
            icon="fast-forward"
            enabled={flags.on_demand.enabled}
            onPress={() => navigateToAutomaticFulfillment("on_demand")}
          />
        )}

        {flags.on_demand.visible && flags.scheduled.visible && <Divider />}

        {flags.scheduled.visible && (
          <Item
            title="Envío diferido"
            description="🗓️ 24-96h. Una recolección diaria. Pasa por centro logístico. Cobertura nacional"
            icon="clock-outline"
            enabled={flags.scheduled.enabled}
            onPress={() => navigateToAutomaticFulfillment("scheduled")}
          />
        )}
      </View>
    </View>
  );
}

function useFulfillmentVisibility() {
  const { fulfillmentOptions, onDemandFulfillmentsFeatureEnabled } =
    useDeliveryFulfillments();

  return fulfillmentOptions.reduce(
    (acc, opt) => {
      if (opt.dispatch_type) {
        // Only show on_demand if the feature flag is enabled
        if (
          opt.dispatch_type === "on_demand" &&
          !onDemandFulfillmentsFeatureEnabled
        ) {
          return acc;
        }

        acc[opt.dispatch_type].visible = true;
        if (opt.published) acc[opt.dispatch_type].enabled = true;
      }

      return acc;
    },
    {
      on_demand: { visible: false, enabled: false },
      scheduled: { visible: false, enabled: false },
      hasOptions: fulfillmentOptions.length > 0,
    }
  );
}

type ItemProps = {
  title: string;
  description: string;
  icon: string;
  enabled?: boolean;
  onPress: () => void;
};

function Item({ title, description, icon, enabled, onPress }: ItemProps) {
  const tailwind = useTailwind();
  const { colors } = useTheme();
  return (
    <List.Item
      title={title}
      description={description}
      descriptionNumberOfLines={5}
      style={tailwind("-mx-4")} // screen is padded with 4px and we want to cover all width
      onPress={onPress}
      left={(props) => <List.Icon {...props} icon={icon} />}
      right={({ style, ...props }) => {
        const color = enabled ? colors.secondary : colors.onSurfaceVariant;
        const text = enabled ? "ACTIVO" : "INACTIVO";
        return (
          <Text
            {...props}
            style={[style, tailwind("self-center"), { color }]}
            variant="labelSmall"
          >
            {text}
          </Text>
        );
      }}
    />
  );
}
